import Image from "next/image";
import CTALink from "@/components/CTA/ctaLink";
import LocalizedOfferEndsPrefix from "@/components/PromoBanner/components/LocalizedOfferEndsPrefix";
import PromoBannerTooltip from "@/components/PromoBanner/components/PromoBannerTooltip";
import type { SlimV2PromoBannerProps } from "@/components/PromoBanner/types";
import boldTextWithAsterisk from "@/utils/functions/boldTextWithAsterisk";
import { cx } from "./constants";

export default function PromoBannerSlimV2({
  backgroundImage,
  backgroundType,
  className,
  cta,
  ctaElement,
  description,
  discountCode,
  endDate,
  endDatePrefix,
  image,
  learnMoreLink,
  title,
  tooltip,
}: SlimV2PromoBannerProps) {
  const withBackgroundTreatment =
    className?.includes("promo-banner-with-bg") ?? false;
  const withBackgroundImage =
    backgroundType === "image" && !!backgroundImage?.url;
  const withSolidBackgroundTreatment =
    backgroundType === "solid" || withBackgroundTreatment;

  return (
    <div
      className={`relative overflow-hidden bg-primary rounded-xl ${cx("container")} ${className || ""}`}
    >
      {withBackgroundImage && backgroundImage && (
        <Image
          src={backgroundImage.url}
          alt=""
          fill
          className="absolute inset-0 object-cover"
        />
      )}
      {withSolidBackgroundTreatment && (
        <div className="absolute inset-0 bg-black opacity-10" />
      )}

      <div className={`relative z-10 flex h-full ${cx("inner")}`}>
        <div className="flex min-w-0 flex-1 flex-col">
          <h2
            className={`text-primary-foreground italic mt-0 mb-5 font-black leading-[1.1] ${cx("title")}`}
          >
            {title}
          </h2>
          <p
            className={`text-primary-foreground leading-[1.4] ${cx("description")}`}
          >
            {typeof description === "string"
              ? boldTextWithAsterisk(description)
              : description}
          </p>
          <div className={`flex flex-col ${cx("ctaRow")}`}>
            {cta && !ctaElement && (
              <CTALink
                type="brand"
                size="xl"
                className={`py-0 ${cx("cta")}`}
                label={cta.label}
                url={cta.url}
                location={cta.location}
                html_id={cta.html_id}
              />
            )}
            {ctaElement && (
              <div
                className={`flex items-center whitespace-nowrap ${cx("ctaSlot")}`}
              >
                {ctaElement}
              </div>
            )}
            <div
              className={`flex items-center justify-start gap-1 ${cx("endDateRow")}`}
            >
              <p className="text-primary-foreground text-xs mb-0">
                <LocalizedOfferEndsPrefix
                  prefix={endDatePrefix}
                  date={endDate}
                />
                .
              </p>
              <PromoBannerTooltip
                discountCode={discountCode}
                endDate={endDate}
                learnMoreLink={learnMoreLink}
                htmlContent={tooltip?.infoText ?? ""}
                size={12}
              />
            </div>
          </div>
        </div>
        {image?.url && (
          <div
            className={`relative shrink-0 overflow-hidden rounded-2xl ${cx("imageBox")}`}
          >
            <Image
              src={image.url}
              alt={image.altText || ""}
              className={`object-cover ${cx("imageFill")}`}
              fill
            />
          </div>
        )}
      </div>
    </div>
  );
}
